import * as z from "zod";
import { pagination } from "../index";
import { atpWaitingReasons } from "./enums";
import { MAX_MOVEMENT_LINES } from "./stock-movement";

// Availability from the IMS (M8, FR-M8-01…04, I-12, ADR-123; 50-api/logistics.md): the last quantity the IMS sent for
// each SKU and warehouse, with its time. Nexxt keeps no stock of its own (FR-M8-02).

/** Part of a code, case-insensitive and without separators (ADR-117). */
const codeQuery = z.string().trim().min(1).max(80);

/** The availability of a warehouse or of an SKU, by page. */
export const listAvailabilityInput = z.object({
  location: z.uuid().optional(),
  sku: z.uuid().optional(),
  /** Part of the code or of the EAN of the SKU. */
  q: codeQuery.optional(),
  sort: z.enum(["skuCode", "-imsUpdatedAt"]).default("skuCode"),
  ...pagination({ defaultSize: 50, maxSize: 200 }),
});
export type ListAvailabilityInput = z.input<typeof listAvailabilityInput>;

/** What the IMS last said of an SKU in a warehouse. */
export const availabilityDto = z
  .object({
    sku: z.object({ id: z.uuid(), skuCode: z.string().nullable(), ean: z.string().nullable() }),
    location: z.object({
      id: z.uuid(),
      code: z.string(),
      name: z.string(),
      /** `reference.legal_entity.code`: a warehouse is of one company (Q-INT-17). */
      company: z.string(),
    }),
    /** Units that can be allocated, already net of the stock of the other channels (FR-M8-04). */
    atp: z.number().int(),
    /**
     * Units of an inbound in this warehouse, received beyond its tolerance, on hold until the supplier invoices them
     * (FR-M11-11, ADR-145): the allocation and the movements leave them out of `atp`, unless the IMS already does
     * (`stock.subtract_units_on_hold`).
     */
    unitsOnHold: z.number().int().min(0),
    /** When the IMS reconciled this quantity: always shown (FR-M8-03). */
    imsUpdatedAt: z.iso.datetime(),
    /** When Nexxt received it: a detail of the channel, which a field rule may hide. */
    receivedAt: z.iso.datetime().optional(),
  })
  .meta({ id: "Availability" });
export type AvailabilityDto = z.output<typeof availabilityDto>;

/** The longest code of a line of a movement read from a file: the column of the SKU code (the EAN is shorter). */
export const MAX_MOVEMENT_CODE = 80;

/**
 * The SKUs a list of codes names in a warehouse, for the lines of a new movement read from a file or a paste (ADR-178
 * §3.3, ADR-190): each code exactly as the SKU code or the EAN, at most the lines a movement holds. A read: `POST`
 * only because the codes do not fit in a URL.
 */
export const resolveMovementSkusInput = z.object({
  location: z.uuid(),
  codes: z.array(z.string().trim().min(1).max(MAX_MOVEMENT_CODE)).min(1).max(MAX_MOVEMENT_LINES),
});
export type ResolveMovementSkusInput = z.input<typeof resolveMovementSkusInput>;

/**
 * Why a code gives no line: no SKU has it as code or EAN; it is the code of one SKU and the EAN of another; the IMS
 * never sent the availability of its SKU in the warehouse, so nothing of it can leave from there.
 */
export const movementSkuMisses = ["unknown_code", "ambiguous_code", "not_in_warehouse"] as const;
export type MovementSkuMiss = (typeof movementSkuMisses)[number];

/** A code that names an SKU of the warehouse, with what the IMS said it has there, as the picker of the SKUs shows. */
export const movementSkuDto = z
  .object({
    code: z.string(),
    sku: z.object({ id: z.uuid(), skuCode: z.string().nullable(), ean: z.string().nullable() }),
    atp: z.number().int(),
    unitsOnHold: z.number().int().min(0),
  })
  .meta({ id: "MovementSku" });
export type MovementSkuDto = z.output<typeof movementSkuDto>;

/** Every code given once, in its first order: the ones that name an SKU, and the ones that do not, with why. */
export const movementSkusDto = z
  .object({
    found: z.array(movementSkuDto),
    missing: z.array(z.object({ code: z.string(), reason: z.enum(movementSkuMisses) })),
  })
  .meta({ id: "MovementSkus" });
export type MovementSkusDto = z.output<typeof movementSkusDto>;

const waitingFilters = {
  reason: z.enum(atpWaitingReasons).optional(),
  /** The warehouse in the code of the IMS. */
  locationCode: z.string().trim().min(1).max(40).optional(),
};

/** The rows of the availability Nexxt could not resolve (I-12 §5), the latest first. */
export const listWaitingAvailabilityInput = z.object({
  ...waitingFilters,
  ...pagination({ defaultSize: 50, maxSize: 200 }),
});
export type ListWaitingAvailabilityInput = z.input<typeof listWaitingAvailabilityInput>;

/** How many rows wait with the same filters, and since when the oldest (ADR-086): the queue of the home. */
export const countWaitingAvailabilityInput = z.object(waitingFilters);
export type CountWaitingAvailabilityInput = z.input<typeof countWaitingAvailabilityInput>;

/**
 * A row of the availability that waits: the latest the IMS sent for that warehouse and item. It goes once a newer
 * publication resolves it, after the mapping or the SKU is fixed.
 */
export const waitingAvailabilityDto = z
  .object({
    id: z.uuid(),
    locationCode: z.string(),
    /** The codes of the item as the IMS sent them. */
    item: z.object({ bcItemId: z.string().nullable(), skuCode: z.string().nullable(), ean: z.string().nullable() }),
    atp: z.number().int(),
    computedAt: z.iso.datetime(),
    reason: z.enum(atpWaitingReasons),
    /** Since when it waits. */
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .meta({ id: "WaitingAvailability" });
export type WaitingAvailabilityDto = z.output<typeof waitingAvailabilityDto>;

/** The permission that marks a mismatch checked (B-92, ADR-242): Logistics, who looks into it with the IMS. */
export const STOCK_MISMATCH_RESOLVE = "stock.mismatch_resolve";

/** Whether a mismatch still waits for Logistics, or was marked checked. */
export const availabilityMismatchStates = ["open", "resolved"] as const;
export type AvailabilityMismatchState = (typeof availabilityMismatchStates)[number];

/**
 * The SKUs a full publication of the IMS found different from what arrived by events (B-16, B-92, ADR-242), the
 * latest first: by default the ones Logistics has not checked yet.
 */
export const listAvailabilityMismatchesInput = z.object({
  location: z.uuid().optional(),
  state: z.enum(availabilityMismatchStates).default("open"),
  ...pagination({ defaultSize: 50, maxSize: 200 }),
});
export type ListAvailabilityMismatchesInput = z.input<typeof listAvailabilityMismatchesInput>;

/** How many mismatches still wait, of a warehouse or of all, and since when the oldest (ADR-086): a queue of the home. */
export const countAvailabilityMismatchesInput = z.object({ location: z.uuid().optional() });
export type CountAvailabilityMismatchesInput = z.input<typeof countAvailabilityMismatchesInput>;

/** Mismatches marked checked in one call at most: four pages of the list. */
export const MAX_RESOLVED_MISMATCHES = 200;

/** Logistics marks mismatches checked: one already checked stays as it was. */
export const resolveAvailabilityMismatchesInput = z.object({
  ids: z.array(z.uuid()).min(1).max(MAX_RESOLVED_MISMATCHES),
});
export type ResolveAvailabilityMismatchesInput = z.input<typeof resolveAvailabilityMismatchesInput>;

/** How many of the ids were still open and are now checked. */
export const availabilityMismatchesResolvedDto = z
  .object({ resolved: z.number().int().min(0) })
  .meta({ id: "AvailabilityMismatchesResolved" });
export type AvailabilityMismatchesResolvedDto = z.output<typeof availabilityMismatchesResolvedDto>;

/**
 * An SKU of a warehouse whose quantity a full publication found different from the one Nexxt had by events: a message
 * of the IMS lost on the way, or one still on its way. `fullQty` is what Nexxt has now, unless a later event changed it.
 */
export const availabilityMismatchDto = z
  .object({
    id: z.uuid(),
    sku: z.object({ id: z.uuid(), skuCode: z.string().nullable(), ean: z.string().nullable() }),
    location: z.object({ id: z.uuid(), code: z.string(), name: z.string() }),
    /** What Nexxt had from the events: 0 when the IMS never sent the SKU in the warehouse. */
    eventQty: z.number().int(),
    /** When the IMS computed that quantity: null when there was none. */
    eventAt: z.iso.datetime().nullable(),
    /** What the full publication says: 0 for an SKU it does not name. */
    fullQty: z.number().int(),
    /** When the IMS computed the full publication. */
    computedAt: z.iso.datetime(),
    publicationId: z.string(),
    state: z.enum(availabilityMismatchStates),
    resolvedAt: z.iso.datetime().nullable(),
    /** Since when it waits. */
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .meta({ id: "AvailabilityMismatch" });
export type AvailabilityMismatchDto = z.output<typeof availabilityMismatchDto>;
